import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { OperationSegmentSpec, WorkInstructionSpec } from '../../process-plan'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AttachmentSpec } from '../../../../attachments/spec/attachment-spec'
import { InstructionBrowserComponent } from '../instruction-browser/instruction-browser.component'
import { ElderHeaderComponent, ElderThemeDirective } from '@elderbyte/ngx-starter'
import { MatToolbar } from '@angular/material/toolbar'
import { MatIcon } from '@angular/material/icon'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatIconButton } from '@angular/material/button'

export interface IInstructionBrowserDialogOptions {
  readonly operation: OperationSegmentSpec
}

@Component({
  selector: 'app-instruction-browser-dialog',
  templateUrl: './instruction-browser-dialog.component.html',
  styleUrls: ['./instruction-browser-dialog.component.scss'],
  imports: [
    MatToolbar,
    MatIcon,
    MatIconButton,
    ElderTranslatePipe,
    ElderHeaderComponent,
    ElderThemeDirective,
    InstructionBrowserComponent,
  ],
})
export class InstructionBrowserDialogComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly original: OperationSegmentSpec
  public readonly copy: OperationSegmentSpec
  private dialogRef = inject<MatDialogRef<InstructionBrowserDialogComponent>>(MatDialogRef)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const data = inject<IInstructionBrowserDialogOptions>(MAT_DIALOG_DATA)
    this.copy = new OperationSegmentSpec()
    this.original = data.operation
    this.update(this.copy, this.original)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onClose($event: Event): void {
    this.close()
  }

  public onSave($event: Event): void {
    this.update(this.original, this.copy)
    this.close(this.original)
  }

  public close(operation?: OperationSegmentSpec): void {
    this.dialogRef.close(operation)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private update(target: OperationSegmentSpec, source: OperationSegmentSpec): void {
    if (source?.action) {
      target.action = source.action
    }
    if (source?.instructions) {
      target.instructions = source.instructions.map((i) => this.copyInstruction(i))
    }
  }

  private copyInstruction(instruction: WorkInstructionSpec): WorkInstructionSpec {
    const copy = new WorkInstructionSpec()
    copy.taskName = instruction.taskName
    copy.taskDescription = instruction.taskDescription
    copy.attachmentSpecs = instruction?.attachmentSpecs?.map((as) => this.copyAttachmentSpec(as))
    return copy
  }

  private copyAttachmentSpec(attachment: AttachmentSpec): AttachmentSpec {
    const attachmentSpec = new AttachmentSpec()
    attachmentSpec.type = attachment.type
    attachmentSpec.typeId = attachment.typeId
    attachmentSpec.scope = attachment.scope
    attachmentSpec.labels = [...attachment.labels]
    return attachmentSpec
  }
}
